import { formatISO } from "date-fns";
import { z } from "zod";
import type { Appointment } from "@/types/admin-portal/appointment";

const isValidDate = (date: Date | null | undefined): date is Date => {
	return date instanceof Date && !Number.isNaN(date.getTime());
};

const THERAPIST_SCHEMA = z.object({
	id: z.string(),
	name: z.string(),
	registrationNumber: z.string().optional(),
	employmentType: z.string().optional(),
	gender: z.string().optional(),
	locationCity: z.string().optional(),
});

// * define the form schema for bulk reschedule
export const BULK_RESCHEDULE_ITEM_SCHEMA = z.object({
	appointmentId: z.string(),
	appointmentDateTime: z.coerce.date().nullable(),
	therapist: THERAPIST_SCHEMA.nullable(),
	preferredTherapistGender: z.string().nullable(),
});

export const BULK_RESCHEDULE_FORM_SCHEMA = z.object({
	rescheduleRequests: z.array(BULK_RESCHEDULE_ITEM_SCHEMA).min(1),
	reason: z.string().optional(),
	sendEmailNotification: z.boolean(),
	bypassSlotConflict: z.boolean().default(false),
	bypassContractCheck: z.boolean().default(false),
	bypassJourneyCheck: z.boolean().default(false),
});

export type BulkRescheduleItemSchema = z.infer<
	typeof BULK_RESCHEDULE_ITEM_SCHEMA
>;
export type BulkRescheduleFormSchema = z.infer<
	typeof BULK_RESCHEDULE_FORM_SCHEMA
>;

// * define the form default values
export const defineFormDefaultValues = (
	appointments: Appointment[],
): BulkRescheduleFormSchema => {
	return {
		rescheduleRequests: appointments.map((appt) => ({
			appointmentId: appt.id,
			appointmentDateTime: appt.appointmentDateTime
				? new Date(appt.appointmentDateTime)
				: null,
			therapist: appt.therapist
				? {
						id: appt.therapist.id,
						name: appt.therapist.name,
						registrationNumber: appt.therapist.registrationNumber,
						employmentType: appt.therapist.employmentType,
						gender: appt.therapist.gender,
						locationCity: appt.therapist.activeAddress?.location?.city,
					}
				: null,
			preferredTherapistGender:
				appt.preferredTherapistGender ?? "NO PREFERENCE",
		})),
		reason: "",
		sendEmailNotification: true,
		bypassSlotConflict: false,
		bypassContractCheck: false,
		bypassJourneyCheck: false,
	} satisfies BulkRescheduleFormSchema;
};

// * define the form payload
export interface BulkReschedulePayload {
	appointmentId: string;
	appointmentDateTime: Date | null;
	therapistId: string | null;
}

export interface RescheduleRequestPayload {
	appointment_id: string;
	new_appointment_datetime: string | null;
	new_therapist_id: string | null;
	reason: string | null;
	mitra_assignment_reason?: string;
}

export interface ApiBulkRescheduleBody {
	items: RescheduleRequestPayload[];
	send_email_notification: boolean;
	bypass_slot_conflict?: boolean;
	bypass_contract_check?: boolean;
	bypass_journey_check?: boolean;
}

export interface BulkRescheduleBypassOptions {
	bypassSlotConflict?: boolean;
	bypassContractCheck?: boolean;
	bypassJourneyCheck?: boolean;
}

export const buildPayload = (
	allItems: BulkRescheduleItemSchema[],
	sendEmailNotification = true,
	reason?: string | null,
	bypass: BulkRescheduleBypassOptions = {},
): ApiBulkRescheduleBody => {
	const body: ApiBulkRescheduleBody = {
		items: allItems.map((item) => ({
			appointment_id: item.appointmentId,
			new_appointment_datetime:
				isValidDate(item.appointmentDateTime) && item.appointmentDateTime
					? formatISO(item.appointmentDateTime)
					: null,
			new_therapist_id: item.therapist?.id || null,
			reason: reason || null,
		})),
		send_email_notification: sendEmailNotification,
	};
	if (bypass.bypassSlotConflict) body.bypass_slot_conflict = true;
	if (bypass.bypassContractCheck) body.bypass_contract_check = true;
	if (bypass.bypassJourneyCheck) body.bypass_journey_check = true;
	return body;
};

// * helper to detect changed visits
export const getChangedVisits = (
	currentValues: BulkRescheduleItemSchema[],
	originalValues: BulkRescheduleItemSchema[],
): BulkRescheduleItemSchema[] => {
	return currentValues.filter((current) => {
		const original = originalValues.find(
			(o) => o.appointmentId === current.appointmentId,
		);
		if (!original) return false;

		const currentDate = current.appointmentDateTime;
		const originalDate = original.appointmentDateTime;
		const currentDateValid = isValidDate(currentDate);
		const originalDateValid = isValidDate(originalDate);

		const dateChanged =
			currentDateValid && originalDateValid
				? currentDate.toISOString() !== originalDate.toISOString()
				: currentDateValid !== originalDateValid;
		const therapistChanged = current.therapist?.id !== original.therapist?.id;

		return dateChanged || therapistChanged;
	});
};
